import { ReactNode, useState, useEffect, useCallback } from "react";
import { useLocation, useLoaderData, useFetcher } from "@remix-run/react";
import { EnableCheckoutExtensionReminder } from "./CheckoutExtension";
import Sidebar from "./Sidebar";
import RateUsFab from "./RateUsFab";
import NavigationProgress from "./NavigationProgress";
import { useI18n } from "~/i18n/i18n";

interface LayoutProps {
  children: ReactNode;
  hasAccountIssues?: boolean;
}

// Interface for user data from session
interface UserData {
  name?: string;
  displayName?: string;
  email: string;
  uid?: string;
  isImpersonated?: boolean;
  originalAdmin?: any;
}

export default function Layout({ children, hasAccountIssues }: LayoutProps) {
  const location = useLocation();
  const rootData = useLoaderData<{
    user?: UserData;
    shouldPromptCheckoutExtension?: boolean;
    shopDomain?: string;
  }>();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { locale } = useI18n();
  const isRTL = locale === "he";
  const [isAnimating, setIsAnimating] = useState(false);
  const shopDomain = rootData?.shopDomain ?? null;
  const fetcher = useFetcher();

  const userData = rootData?.user || null;

  const handleCloseMobileMenu = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => setIsMobileMenuOpen(false), 300);
  }, []);

  const handleOpenMobileMenu = () => {
    setIsMobileMenuOpen(true);
    setTimeout(() => setIsAnimating(true), 10);
  };

  // Close mobile menu when route changes
  useEffect(() => {
    if (isMobileMenuOpen) handleCloseMobileMenu();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock body scroll while the mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "unset";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  return (
    <div className="h-screen bg-canvas flex flex-row">
      <NavigationProgress />
      {/* Desktop icon rail — always on the left, including in RTL (Hebrew) */}
      <div className="hidden lg:block">
        <Sidebar userEmail={userData?.email} hasAccountIssues={hasAccountIssues} shopDomain={rootData?.shopDomain} />
      </div>

      <div className="flex flex-col flex-1 min-w-0">
        {/* Mobile top bar — just a menu trigger, the rail slides over */}
        <header className="lg:hidden bg-white h-16 flex items-center justify-between px-4 border-b border-line flex-none">
          <button
            className="p-2 rounded-xl hover:bg-canvas transition-colors"
            onClick={() => (isMobileMenuOpen ? handleCloseMobileMenu() : handleOpenMobileMenu())}
            aria-label="Open menu"
          >
            <div className="w-5 h-5 flex flex-col justify-center space-y-1">
              <div className="w-full h-0.5 bg-ink rounded-full" />
              <div className="w-full h-0.5 bg-ink rounded-full" />
              <div className="w-full h-0.5 bg-ink rounded-full" />
            </div>
          </button>
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-white font-extrabold text-sm"
            style={{ background: "linear-gradient(135deg,#ec4899,#f59e0b)" }}
          >
            {(userData?.email?.trim()?.charAt(0) || "U").toUpperCase()}
          </div>
        </header>

        {/* Impersonation Banner */}
        {userData?.isImpersonated && (
          <div className="bg-orange-100 border-b border-orange-200 px-4 lg:px-6 py-3 flex-none">
            <div className={`flex items-center justify-between ${isRTL ? "flex-row-reverse" : ""}`}>
              <div className={`flex items-center gap-3 ${isRTL ? "flex-row-reverse" : ""}`}>
                <div className={`flex items-center gap-2 text-orange-800 ${isRTL ? "flex-row-reverse" : ""}`}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-6-3a2 2 0 11-4 0 2 2 0 014 0zm-2 4a5 5 0 00-4.546 2.916A5.986 5.986 0 0010 16a5.986 5.986 0 004.546-2.084A5 5 0 0010 11z" clipRule="evenodd" />
                  </svg>
                  <span className="font-semibold">{isRTL ? "מצב חיקוי מנהל" : "Admin Impersonation Mode"}</span>
                </div>
                <div className="text-sm text-orange-700">
                  {isRTL ? "צופה כ:" : "Viewing as:"} <span className="font-medium">{userData.name || userData.email}</span>
                </div>
              </div>
              <fetcher.Form method="post" action="/backoffice" className="inline">
                <input type="hidden" name="action" value="exitImpersonation" />
                <button
                  type="submit"
                  className="px-3 py-1 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 transition-colors"
                  disabled={fetcher.state === "submitting"}
                >
                  {fetcher.state === "submitting"
                    ? isRTL ? "יוצא..." : "Exiting..."
                    : isRTL ? "צא מחיקוי" : "Exit Impersonation"}
                </button>
              </fetcher.Form>
            </div>
          </div>
        )}

        {/* Main Content */}
        <main className="flex-1 overflow-auto bg-canvas">{children}</main>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className={`lg:hidden fixed inset-0 z-50 transition-opacity duration-300 ${isAnimating ? "opacity-100" : "opacity-0"}`}>
          <div className="absolute inset-0 bg-black bg-opacity-50" onClick={handleCloseMobileMenu} />
          <div
            className={`absolute left-0 top-0 h-full transition-transform duration-300 ease-out ${
              isAnimating ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <Sidebar
              onClose={handleCloseMobileMenu}
              userEmail={userData?.email}
              hasAccountIssues={hasAccountIssues}
              shopDomain={shopDomain ?? undefined}
            />
          </div>
        </div>
      )}

      {rootData.shouldPromptCheckoutExtension && <EnableCheckoutExtensionReminder shopDomain={shopDomain ?? undefined} />}

      {/* Rate-us star — shown on every authenticated screen */}
      <RateUsFab />
    </div>
  );
}
